import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// ─── Context value type ───────────────────────────────────────────────────────
const AuthContext = createContext(null);

const TOKEN_KEY = 'daggpt_token';
const USER_KEY = 'daggpt_user';

function decodeJWT(token) {
  try { return JSON.parse(atob(token.split('.')[1])); } catch { return null; }
}
function isExpired(token) {
  const p = decodeJWT(token);
  return !p?.exp || Date.now() >= p.exp * 1000;
}
function getStoredToken() {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t || isExpired(t)) { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); return null; }
  // Purge bad tokens issued before the sb() fix - they have email=null in payload
  const payload = decodeJWT(t);
  if (!payload?.email) { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); localStorage.removeItem('daggpt_authenticated'); return null; }
  return t;
}

// ─── FingerprintJS lazy loader ─────────────────────────────────────────────
async function getFingerprint() {
  try {
    const FpJS = await import('@fingerprintjs/fingerprintjs');
    const fp = await FpJS.default.load();
    const { visitorId } = await fp.get();
    return visitorId;
  } catch { return null; }
}

// ─── API base URL ─────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const fpRef = useRef(null);
  const ssoChecked = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  // Preload fingerprint
  useEffect(() => { getFingerprint().then(fp => { fpRef.current = fp; }); }, []);

  // ── Session initialization ─────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      // 1. Check for SSO token in URL
      if (!ssoChecked.current) {
        ssoChecked.current = true;
        const params = new URLSearchParams(window.location.search);
        const ssoToken = params.get('sso_token');
        if (ssoToken) {
          try {
            const res = await fetch(`${API}/api/auth/sso/validate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: ssoToken }),
            });
            if (res.ok) {
              const data = await res.json();
              // New JWT-based response: { token, user }
              if (data.token && data.user) {
                localStorage.setItem(TOKEN_KEY, data.token);
                localStorage.setItem(USER_KEY, JSON.stringify(data.user));
                localStorage.setItem('daggpt_authenticated', 'true');
                if (isMounted.current) setUser(data.user);
                const clean = new URL(window.location.href);
                clean.searchParams.delete('sso_token');
                window.history.replaceState({}, '', clean.toString());
                setIsLoading(false);
                return;
              }
              // Legacy UUID-based response: { success, user } (no token)
              if (data.success && data.user) {
                localStorage.setItem(USER_KEY, JSON.stringify(data.user));
                localStorage.setItem('daggpt_authenticated', 'true');
                if (isMounted.current) setUser(data.user);
                const clean = new URL(window.location.href);
                clean.searchParams.delete('sso_token');
                window.history.replaceState({}, '', clean.toString());
                setIsLoading(false);
                return;
              }
            } else {
              const err = await res.json().catch(() => ({}));
              console.warn('[DAGGPT AuthContext] SSO validate failed:', res.status, err);
            }
          } catch (e) { console.warn('[DAGGPT AuthContext] SSO validation error:', e); }
        }
      }

      // 2. Always validate token with server before rendering user
      const token = getStoredToken();
      if (token) {
        try {
          const res = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            if (data?.user && isMounted.current) {
              setUser(data.user);
              localStorage.setItem(USER_KEY, JSON.stringify(data.user));
            } else {
              // Server rejected token - clear everything
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(USER_KEY);
              localStorage.removeItem('daggpt_authenticated');
              if (isMounted.current) setUser(null);
            }
          } else {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            localStorage.removeItem('daggpt_authenticated');
            if (isMounted.current) setUser(null);
          }
        } catch {
          // Network error - fall back to cached user so app still works offline
          const cached = localStorage.getItem(USER_KEY);
          if (cached && isMounted.current) {
            try { setUser(JSON.parse(cached)); } catch {}
          }
        }
      } else {
        // No token - clear any stale user cache
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem('daggpt_authenticated');
      }

      if (isMounted.current) setIsLoading(false);
    })();
  }, []);



  // ── Login ──────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, fingerprint_id: fpRef.current }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Login failed' };
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setUser(data.user);
      return { success: true };
    } catch { return { success: false, error: 'Network error' }; }
    finally { setIsLoading(false); }
  }, []);

  // ── Register ───────────────────────────────────────────────────────────────
  const register = useCallback(async (email, password, full_name) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, full_name, fingerprint_id: fpRef.current }),
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Registration failed' };
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setUser(data.user);
      return { success: true, isNewUser: data.isNewUser };
    } catch { return { success: false, error: 'Network error' }; }
    finally { setIsLoading(false); }
  }, []);

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try { await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' }); } catch {}
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  // ── SSO jump ───────────────────────────────────────────────────────────────
  const issueSSOToken = useCallback(async (target) => {
    const token = getStoredToken();
    if (!token) return null;
    try {
      const res = await fetch(`${API}/api/auth/sso/issue-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target }),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  }, []);

  // ── Refresh balance (call after any generation) ───────────────────────────
  const [dgccBalance, setDgccBalance] = useState(null);
  const refreshBalance = useCallback(async (userId) => {
    const uid = userId || user?.id;
    if (!uid) return;
    try {
      const res = await fetch(`${API}/api/billing/balance`, { headers: { 'x-user-id': uid } });
      if (res.ok) { const d = await res.json(); setDgccBalance(d); }
    } catch {}
  }, [user?.id]);

  // ── Auto-refresh balance on login ──────────────────────────────────────────
  useEffect(() => {
    if (user?.id) {
      refreshBalance(user.id);
    }
  }, [user?.id, refreshBalance]);

  // ── Refresh user profile ────────────────────────────────────────────────────
  const refreshUser = useCallback(async () => {
    const token = getStoredToken();
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
      if (res.ok) {
        const { user: fresh } = await res.json();
        setUser(fresh);
        localStorage.setItem(USER_KEY, JSON.stringify(fresh));
      }
    } catch {}
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    // Backward-compat (existing DAGGPT components use these)
    address: user?.wallet_address || null,
    isConnected: !!user,
    userRole: user?.role || null,
    isAdmin: user?.is_admin || false,
    fingerprint: fpRef.current,
    // Navbar-compatible userProfile shape
    userProfile: user ? {
      ...user,
      id:     user.id,                                        // ← Supabase UUID - needed for billing
      name:   user.full_name  || user.name  || user.email || 'User',
      email:  user.email,
      avatar: user.avatar_url || user.avatar || null,
    } : null,
    // Actions
    login,
    register,
    logout,
    issueSSOToken,
    dgccBalance,
    refreshBalance,
    refreshUser,
    getToken: getStoredToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
